import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import { Chart } from 'chart.js';
import { Loader } from '@components';
import AdShareTab from './AdShareTab';
import CohortSlider from './CohortSlider';
import AdImpressionsChart from './AdImpressionsChart';
import { getWeekEnd } from './utils';
import { AdShareResult, ChartData, ChartListData, RawScoreEntry } from './types';
import { AD_IMPRESSIONS_CHART_TYPE } from './constants';
interface AdImpressionsIndexSectionProps {
  chartList: ChartListData[];
  selections: Record<string, string>;
  fetchDashboardData: (
    dashboardData: ChartListData[],
    setChartData: React.Dispatch<ChartData[]>,
    setComponentLoader: React.Dispatch<React.SetStateAction<boolean>>,
    filters?: Record<string, string>,
  ) => Promise<void>;
}

const AdImpressionsIndexSection: React.FC<AdImpressionsIndexSectionProps> = ({
  chartList,
  selections,
  fetchDashboardData,
}) => {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [componentLoader, setComponentLoader] = useState<boolean>(true);

  const chartRef = useRef<ChartJSOrUndefined<'line'>>(null);

  const adIndexData = chartData.find(
    (chart: ChartData) => chart.chartType === AD_IMPRESSIONS_CHART_TYPE.AD_INDEX,
  );
  const adShareData = chartData.find(
    (chart: ChartData) => chart.chartType === AD_IMPRESSIONS_CHART_TYPE.AD_SHARE,
  );
  const adIndexChartData = adIndexData?.result?.data as RawScoreEntry[];
  const SLIDER_DISPLAY_THRESHOLD = 40;
  const weekStartDate = adIndexChartData ? adIndexChartData?.[selectedWeek - 1]?.week_start : '';
  const weekEndDate = weekStartDate ? getWeekEnd(weekStartDate) : '';

  const handleChartClick = (chart: Chart, event: MouseEvent) => {
    const points = chart.getElementsAtEventForMode(event, 'index', { intersect: false }, false);
    if (points.length > 0) {
      setClickedIndex(points[0].index);
    }
  };

  useEffect(() => {
    if (clickedIndex !== null) {
      setSelectedWeek(adIndexChartData[clickedIndex].week_number);
    }
  }, [clickedIndex]);

  useEffect(() => {
    if (adIndexChartData) {
      setSelectedWeek(adIndexChartData?.[adIndexChartData.length - 1]?.week_number || 0);
    }
  }, [adIndexChartData]);

  useEffect(() => {
    if (selections.country && selections.category_genre) {
      const filters = {
        category_genre: selections.category_genre,
        country: selections.country,
      };
      fetchDashboardData(chartList, setChartData, setComponentLoader, filters);
      setClickedIndex(null);
    }
  }, [selections.country]);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 'fit-content',
          borderRadius: '1.25rem',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          paddingX: '1rem',
          paddingY: '1.5rem',
          position: 'relative',
        }}
      >
        {componentLoader ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '440px',
            }}
          >
            <Loader />
          </Box>
        ) : (
          <>
            <AdShareTab
              selectedWeek={{
                week: selectedWeek,
                startDate: weekStartDate,
                endDate: weekEndDate,
              }}
              adShareData={adShareData?.result as AdShareResult}
            />
            <AdImpressionsChart
              chartRef={chartRef}
              handleChartClick={handleChartClick}
              clickedIndex={clickedIndex}
              plotData={adIndexChartData}
            />
            {adIndexChartData?.length > SLIDER_DISPLAY_THRESHOLD && (
              <CohortSlider
                months={adIndexChartData.map((item: { week_start: any }) => item.week_start)}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default AdImpressionsIndexSection;
